/**
 * Draws a plausible-looking web page into a canvas so the mock worker can emit
 * real JPEG frames and real capture PNGs.
 *
 * Why not a static placeholder image: the whole point of the mock is that every
 * UI state downstream — scrolling, capturing at a scroll offset, thumbnails in
 * the rail, the shutter flash — exercises the same code path it will when the
 * worker is live. A frozen PNG would let scroll and capture silently do nothing.
 *
 * Delete this file (and mock-transport.ts) once the worker container is up.
 */

const PAGE_HEIGHT_FACTOR = 3.4;

/** Stable per-host accent, so revisiting a site looks like the same site. */
function hashHue(host: string): number {
  let hash = 0;
  for (const char of host) hash = (hash * 31 + char.charCodeAt(0)) % 360;
  return hash;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.fill();
}

export interface MockPageOptions {
  url: string;
  width: number;
  height: number;
  scrollY: number;
  mobile: boolean;
  /** Device pixel ratio to render at. 1 for the live screencast, 2 for captures. */
  scale?: number;
}

/** Total scrollable height of the fake page, in CSS px. */
export function mockPageHeight(viewportHeight: number): number {
  return Math.round(viewportHeight * PAGE_HEIGHT_FACTOR);
}

/**
 * Renders one frame and returns it as a data URL. Returns null in environments
 * with no canvas implementation (jsdom under Vitest) — callers fall back to a
 * placeholder rather than throwing, so component tests can still drive the
 * session state machine.
 */
export function renderMockPage(options: MockPageOptions): string | null {
  const { height, mobile, scrollY, url, width } = options;
  const scale = options.scale ?? 1;

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;

  const ctx = canvas.getContext('2d');
  // roundRect is missing in jsdom even when a 2d context exists.
  if (!ctx || typeof ctx.roundRect !== 'function') return null;

  ctx.scale(scale, scale);

  let host = url;
  try {
    host = new URL(url).host;
  } catch {
    /* keep the raw string */
  }

  const hue = hashHue(host);
  const accent = `hsl(${String(hue)} 72% 56%)`;
  const accentSoft = `hsl(${String(hue)} 80% 96%)`;
  const pad = mobile ? 20 : 64;
  const y = (top: number) => top - scrollY;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  // ── Sticky top nav ─────────────────────────────────────────────────────────
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, 64);
  ctx.fillStyle = '#eceae6';
  ctx.fillRect(0, 63, width, 1);

  ctx.fillStyle = accent;
  roundRect(ctx, pad, 22, 20, 20, 6);
  ctx.fillStyle = '#1a1a1a';
  ctx.font = '600 15px system-ui, sans-serif';
  ctx.fillText(host, pad + 30, 37);

  if (!mobile) {
    ctx.fillStyle = '#dedcd8';
    for (let i = 0; i < 4; i += 1) {
      roundRect(ctx, width - pad - 300 + i * 74, 27, 52, 9, 4);
    }
  } else {
    ctx.fillStyle = '#dedcd8';
    for (let i = 0; i < 3; i += 1)
      roundRect(ctx, width - pad - 22, 24 + i * 7, 22, 3, 2);
  }

  // ── Hero ───────────────────────────────────────────────────────────────────
  ctx.fillStyle = accentSoft;
  roundRect(ctx, 0, y(64), width, 380, 0);

  ctx.fillStyle = accent;
  roundRect(ctx, pad, y(128), mobile ? 96 : 120, 22, 11);

  ctx.fillStyle = '#1a1a1a';
  const titleSize = mobile ? 30 : 46;
  ctx.font = `700 ${String(titleSize)}px system-ui, sans-serif`;
  ctx.fillText('Everything you need,', pad, y(128 + 76));
  ctx.fillText('in one place.', pad, y(128 + 76 + titleSize + 10));

  ctx.fillStyle = '#8d8d8d';
  ctx.font = '400 16px system-ui, sans-serif';
  ctx.fillText(
    'A simulated page, served by the mock browser worker.',
    pad,
    y(300),
  );

  ctx.fillStyle = '#1a1a1a';
  roundRect(ctx, pad, y(330), 150, 44, 10);
  ctx.fillStyle = '#ffffff';
  ctx.font = '600 14px system-ui, sans-serif';
  ctx.fillText('Get started', pad + 36, y(357));

  // ── Card grid ──────────────────────────────────────────────────────────────
  const cols = mobile ? 1 : 3;
  const gap = 24;
  const cardW = (width - pad * 2 - gap * (cols - 1)) / cols;
  for (let i = 0; i < 6; i += 1) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cardX = pad + col * (cardW + gap);
    const cardY = y(520 + row * 220);

    ctx.fillStyle = '#f8f8f6';
    roundRect(ctx, cardX, cardY, cardW, 190, 18);
    ctx.fillStyle = i % 3 === 0 ? accent : '#dedcd8';
    roundRect(ctx, cardX + 20, cardY + 20, 36, 36, 10);
    ctx.fillStyle = '#c9c7c3';
    roundRect(ctx, cardX + 20, cardY + 76, cardW * 0.6, 12, 6);
    roundRect(ctx, cardX + 20, cardY + 100, cardW * 0.82, 8, 4);
    roundRect(ctx, cardX + 20, cardY + 116, cardW * 0.74, 8, 4);
  }

  // ── Footer ─────────────────────────────────────────────────────────────────
  const pageHeight = mockPageHeight(height);
  ctx.fillStyle = '#1a1a1a';
  ctx.fillRect(0, y(pageHeight - 220), width, 220);
  ctx.fillStyle = '#3a3a3a';
  for (let i = 0; i < 3; i += 1) {
    roundRect(ctx, pad + i * 140, y(pageHeight - 160), 100, 10, 5);
  }

  try {
    return canvas.toDataURL('image/jpeg', 0.72);
  } catch {
    return null;
  }
}

/** Strips the `data:image/jpeg;base64,` prefix — the wire carries bare base64. */
export function toBase64(dataUrl: string): string {
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}
